/**
 * media-upload — put a picked image through the real upload op and hand back
 * the URL to store on the record (2026-08-07, Pat).
 *
 * `<ImageDropField>` gives us a data URL straight off the file picker. Two
 * things have to happen before that can be a record field:
 *
 *  1. DOWNSCALE. A phone photo is 3–6 MB; nothing in a roster row needs more
 *     than an avatar. We cap the long edge at 512px and re-encode, which lands
 *     typical avatars at 30–80 KB — small enough that the upload is instant and
 *     the stored asset is cheap wherever it ends up.
 *  2. UPLOAD. The bytes go to `uploadMediaAsset` (POST /v1/admin/media/uploads)
 *     and the returned `url` is what the record carries. The image is never
 *     smuggled into the record payload itself.
 *
 * Anything already stored (an http(s) URL, or a URL we got back from a previous
 * upload) passes straight through — re-saving a record must not re-upload the
 * same photo.
 */
import { uploadMediaAsset } from "@v0y4ge/api-client";

const MAX_EDGE = 512;
/** The server's own cap (`BRANDING_LOGO_MAX_BYTES`), checked here so the user
 *  learns the limit instead of reading a generic failure after a round-trip. */
const SERVER_MAX_BYTES = 2 * 1024 * 1024;
/** Mirrors the field's own `REFUSE_BYTES`, so a huge pick is never materialised. */
const REFUSE_BYTES = 25_000_000;

function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type MediaPurpose = "staff-photo" | "resource-photo" | "service-hero" | "brand-asset" | "other";

function isStoredUrl(value: string): boolean {
  return /^(https?:)?\/\//i.test(value) || value.startsWith("/");
}

/**
 * Read a `blob:` object URL back into a data URL (CD docket 2026-09-15,
 * service hero image).
 *
 * An object URL only resolves inside the tab that minted it, so persisting one
 * on a record means the field reads back blank on the next load — the PATCH
 * answers 200 and nothing looks wrong. Fields that hand us a `blob:` value
 * therefore have to be resolved to bytes before upload, not passed through.
 *
 * Throws on a revoked or non-image object URL, so the caller shows its failure
 * state instead of "saving" a dead URL.
 */
async function blobUrlToDataUrl(blobUrl: string): Promise<string> {
  const response = await fetch(blobUrl);
  if (!response.ok) throw new Error(`object URL could not be read (${response.status})`);
  const contentType = String(response.headers.get("Content-Type") || "").split(";")[0].trim();
  if (!/^image\//i.test(contentType)) throw new Error(`object URL is not an image (${contentType || "no content type"})`);
  /* A blob: response carries the Blob's real size, so an oversized pick is
   * refused BEFORE arrayBuffer() turns 25 MB of bytes into a ~33 MB base64
   * string in the tab (CD docket 2026-09-15, size guard). */
  const declared = Number(response.headers.get("Content-Length") || 0);
  if (Number.isFinite(declared) && declared > REFUSE_BYTES) {
    throw new Error(`Image is ${mb(declared)}; the most that can be uploaded is ${mb(REFUSE_BYTES)}.`);
  }
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  /* Chunked so a multi-megabyte pick cannot blow the argument limit of
   * String.fromCharCode with one spread of every byte. */
  const CHUNK = 32 * 1024;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as unknown as number[]);
  }
  return `data:${contentType};base64,${btoa(binary)}`;
}

/** Draw into a canvas at <= MAX_EDGE and re-encode. Falls back to the original. */
/** The upload contract's three accepted types, and all `downscale` may return. */
type UploadMime = "image/png" | "image/jpeg" | "image/webp";

async function downscale(dataUrl: string, maxEdge: number, keepAlpha: boolean): Promise<{ data: string; contentType: UploadMime }> {
  /* PNG only when the caller WANTS alpha and the source actually carries it.
   * A lossless PNG at 1600px routinely exceeds the server's 2 MB cap
   * (MEDIA_FILE_TOO_LARGE, 413), which is why the hero passes keepAlpha:false
   * and ships as JPEG. */
  const wantsAlpha = keepAlpha && (dataUrl.startsWith("data:image/png") || dataUrl.startsWith("data:image/webp"));
  const type = wantsAlpha ? "image/png" : "image/jpeg";
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image decode failed"));
      el.src = dataUrl;
    });
    const edge = Math.max(img.naturalWidth, img.naturalHeight) || maxEdge;
    const scale = edge > maxEdge ? maxEdge / edge : 1;
    const w = Math.max(1, Math.round((img.naturalWidth || maxEdge) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || maxEdge) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { data: dataUrl, contentType: type };
    ctx.drawImage(img, 0, 0, w, h);
    return { data: canvas.toDataURL(type, 0.85), contentType: type };
  } catch (e) {
    console.warn("media downscale failed, uploading original:", e);
    /* Was `m[1]` — the source mime, passed through verbatim. On the happy path a GIF
     * is re-encoded to JPEG and never reaches the server as GIF, so the only way
     * `image/gif` got out was here, when decode failed. The server's mime normalizer
     * has no GIF entry, so that upload came back 415. Only report a type the contract
     * accepts; anything else is the original bytes, which we describe as the closest
     * accepted type rather than one the server will reject. */
    const m = /^data:([^;,]+)/.exec(dataUrl);
    const source = m ? m[1] : type;
    const accepted: UploadMime[] = ["image/png", "image/jpeg", "image/webp"];
    return { data: dataUrl, contentType: accepted.includes(source as UploadMime) ? (source as UploadMime) : type };
  }
}

/**
 * Returns the URL to store, or "" when the field was cleared.
 * Throws when the upload fails — the caller must NOT pretend it saved.
 */
export async function uploadImageField(value: string | null | undefined, opts: {
  purpose: MediaPurpose;
  ownerId?: string | null;
  filename?: string;
  /** Long-edge cap. Defaults to the avatar size; a hero passes 1600. */
  maxEdge?: number;
  /** Keep a PNG source lossless. Defaults true; a hero passes false so a large
   *  pick cannot exceed the server's 2 MB cap. */
  keepAlpha?: boolean;
}): Promise<string> {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  if (isStoredUrl(raw)) return raw;
  /* Before the data-URL path, because a blob: value IS uploadable once read —
   * it just cannot be stored. */
  const resolved = raw.startsWith("blob:") ? await blobUrlToDataUrl(raw) : raw;
  if (!resolved.startsWith("data:image/")) return resolved; // not something we can upload; leave it alone
  const { data, contentType } = await downscale(resolved, opts.maxEdge ?? MAX_EDGE, opts.keepAlpha ?? true);
  const sizeBytes = Math.round((data.length - (data.indexOf(",") + 1)) * 3 / 4);
  /* Check the server's cap BEFORE sending. The server rejects an over-cap image
   * with MEDIA_FILE_TOO_LARGE 413, but the user only ever saw "couldn't be
   * uploaded" and could not tell that a smaller picture would work. Naming the
   * two numbers is the whole point of the message. */
  if (sizeBytes > SERVER_MAX_BYTES) {
    throw new Error(`Image is ${mb(sizeBytes)} after resizing; the limit is ${mb(SERVER_MAX_BYTES)}. Try a smaller or less detailed picture.`);
  }
  const asset = await uploadMediaAsset({
    purpose: opts.purpose,
    ownerId: opts.ownerId ?? null,
    filename: opts.filename || `${opts.purpose}.${contentType === "image/png" ? "png" : "jpg"}`,
    /* No cast: `downscale` now returns `UploadMime`, which is the contract's union.
     * `image/gif` used to be asserted here — type-valid by construction, so nothing
     * failed until the server answered 415. */
    contentType,
    data,
    sizeBytes,
  });
  return asset.url;
}
